import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Search, PenTool, Hammer, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { getServiceBySlug, services } from "@/data/services";
import NotFound from "@/pages/NotFound";

const process = [
  {
    icon: Search,
    title: "Discover",
    text: "We start by understanding your business goals, constraints, and current systems before writing a line of code.",
  },
  {
    icon: PenTool,
    title: "Design & Plan",
    text: "Architecture, scope, and milestones get defined up front, so delivery is predictable and accountable.",
  },
  {
    icon: Hammer,
    title: "Build & Test",
    text: "Senior engineers build in short, reviewable increments with automated testing baked into every release.",
  },
  {
    icon: LifeBuoy,
    title: "Launch & Support",
    text: "We ship to production and stay engaged for tuning, monitoring, and ongoing enhancement.",
  },
];

export default function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const service = getServiceBySlug(slug);

  if (!service) return <NotFound />;

  const related = services.filter((s) => service.relatedSlugs.includes(s.slug));

  return (
    <>
      <Seo
        title={`${service.title} — Stantive Soft Solutions`}
        description={service.description}
        path={`/services/${service.slug}`}
      />

      <PageHero
        eyebrow="Service practice"
        image="/images/hero-services.jpg"
        imagePosition="center"
        title={service.title}
        description={service.description}
        actions={
          <>
            <Button
              asChild
              size="lg"
              className="bg-gradient-hero text-primary-foreground shadow-elegant hover:opacity-90"
            >
              <Link to="/contact">
                Get a Quote <ArrowRight size={16} className="ml-1" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-ink-foreground/25 bg-ink/25 text-ink-foreground backdrop-blur hover:bg-ink-foreground/10"
            >
              <Link to="/services">
                <ArrowLeft size={16} className="mr-2" />
                All Services
              </Link>
            </Button>
          </>
        }
        stats={[
          { value: `${service.bullets.length}`, label: "Capabilities" },
          { value: "4", label: "Stages" },
          { value: "Senior", label: "Delivery" },
          { value: "Measured", label: "Outcomes" },
        ]}
      />

      <section className="py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1.25fr_0.75fr] lg:px-8">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Practice overview
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              Built around practical delivery and measurable value
            </h2>
            <div className="mt-6 space-y-4 leading-relaxed text-muted-foreground">
              {service.overview.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="h-full rounded-2xl bg-gradient-ink p-8 shadow-elegant">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary-glow">
                Outcomes
              </span>
              <ul className="mt-5 space-y-4">
                {service.outcomes.map((outcome) => (
                  <li key={outcome} className="flex items-start gap-3 text-sm text-ink-muted">
                    <div className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md bg-primary-glow/15 text-primary-glow">
                      <Check size={14} />
                    </div>
                    <span className="leading-relaxed">{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              What's included
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              Everything you need, one accountable team
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {service.bullets.map((b, i) => (
              <Reveal key={b} delay={i * 60}>
                <div className="group flex h-full items-start gap-3 rounded-2xl border border-border bg-card p-5 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:bg-ink hover:shadow-elegant">
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary-glow/15 group-hover:text-primary-glow">
                    <Check size={16} />
                  </div>
                  <span className="text-sm font-medium leading-snug transition-colors group-hover:text-ink-foreground">
                    {b}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              How we work
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              A proven, predictable process
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {process.map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <div className="group h-full rounded-2xl border border-border bg-card p-6 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:bg-ink hover:shadow-elegant">
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary transition-colors group-hover:text-primary-glow">
                    0{i + 1}
                  </span>
                  <div className="mt-3 grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary-glow/15 group-hover:text-primary-glow">
                    <p.icon size={20} />
                  </div>
                  <h3 className="mt-4 font-semibold transition-colors group-hover:text-ink-foreground">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground transition-colors group-hover:text-ink-muted">
                    {p.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="max-w-2xl">
              <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                Related services
              </span>
              <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
                Often paired with {service.title.toLowerCase()}
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              {related.map((r, i) => (
                <Reveal key={r.slug} delay={i * 80}>
                  <Link
                    to={`/services/${r.slug}`}
                    className="group block h-full rounded-2xl border border-border bg-card p-6 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:bg-ink hover:shadow-elegant"
                  >
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-hero text-primary-foreground shadow-elegant transition-transform duration-300 group-hover:scale-105">
                      <r.icon size={20} />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold transition-colors group-hover:text-ink-foreground">
                      {r.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground transition-colors group-hover:text-ink-muted">
                      {r.shortDescription}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-all group-hover:gap-2.5 group-hover:text-primary-glow">
                      Learn more <ArrowRight size={14} />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl bg-gradient-ink p-10 shadow-elegant sm:p-16">
          <div className="relative grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <h2 className="font-display text-3xl font-bold text-ink-foreground sm:text-4xl">
                Ready to start your {service.title.toLowerCase()} project?
              </h2>
              <p className="mt-3 max-w-xl text-ink-muted">
                Tell us about your goals. We'll respond within one business day with a concrete next
                step.
              </p>
            </div>
            <Button
              asChild
              size="lg"
              className="w-fit bg-gradient-hero text-primary-foreground hover:opacity-90"
            >
              <Link to="/contact">
                Get in Touch <ArrowRight size={16} className="ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
